// Cómo se pinta la agenda: los rótulos visibles del mock, uno por uno. Nada de esto se acorta ni
// se reescribe — el mock manda hasta la última coma.
import type { CreditTone } from '@/domains/credit/creditFile/logic';
import type { StatsBarItem } from '@/shared/patterns/StatsBar';
import { AGENDA_STAT_KEYS } from './types';
import type {
    AgendaCreditStatus,
    AgendaStatKey,
    AgendaStats,
    ContactChannel,
    ContactChannelFilter,
    ContactOutcome,
    PaymentChannel,
    PriorityRank,
    PromiseState,
} from './types';

/** El pesos-y-centavos del mock (`SK.fmt`). Las filas guardan centavos, como todo el front. */
const MONEY = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
});

export function formatAgendaMoney(cents: number): string {
    return MONEY.format(cents / 100);
}

/** El día corto del mock: `08 jul 26`. */
export function formatShortDay(day: string): string {
    const parsed = new Date(`${day.slice(0, 10)}T00:00:00`);
    return parsed.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' });
}

/** Los rótulos de las 9 columnas, en el orden del mock. El test los ancla contra el DOM. */
export const AGENDA_COLUMN_LABELS: readonly string[] = [
    'Prioridad',
    'CR',
    'Cliente',
    'C.V.',
    'Vencido',
    'DPI',
    'Últ. contacto',
    'Intentos',
    'Compromiso',
];

/** Los rótulos de los 6 grupos del panel de filtros, en el orden del mock. */
export const AGENDA_FILTER_GROUP_LABELS: readonly string[] = [
    'Búsqueda',
    'Tienda',
    'Días de mora',
    'Canal último contacto',
    'Monto vencido',
    'Extra',
];

export const AGENDA_EMPTY_MESSAGE = 'Ningún crédito en agenda coincide con los filtros.';

export const AGENDA_STAT_LABEL: Record<AgendaStatKey, string> = {
    all: 'Agenda',
    broken: 'Incumplidos',
    today: 'Compromisos hoy',
    over30: 'Mora >30d',
    locked: 'Bloqueados',
};

export const AGENDA_STAT_TONE: Record<AgendaStatKey, CreditTone> = {
    all: 'neutral',
    broken: 'danger',
    today: 'info',
    over30: 'danger',
    locked: 'danger',
};

/** Las 5 pastillas de la agenda para la `<StatsBar>`, en el orden del mock. */
export function agendaStatsItems(stats: AgendaStats): StatsBarItem<AgendaStatKey>[] {
    return AGENDA_STAT_KEYS.map((key) => ({
        key,
        label: AGENDA_STAT_LABEL[key],
        value: stats[key],
        tone: AGENDA_STAT_TONE[key],
    }));
}

/** El tono de cada rango de prioridad, tal cual las clases `.prio-N` del mock. */
export const PRIORITY_TONE: Record<PriorityRank, CreditTone> = {
    1: 'danger',
    2: 'info',
    3: 'danger',
    4: 'warning',
    5: 'info',
};

/** Los 5 canales que ofrece el cajón «Registrar contacto». */
export const CONTACT_CHANNEL_LABEL: Record<ContactChannel, string> = {
    call: 'Llamada telefónica',
    sms: 'SMS',
    chat: 'Chat App Fiado',
    whatsapp: 'WhatsApp',
    voice_blaster: 'Voz automatizada (blaster)',
};

/** Los mismos canales, cortos, para el historial y la tabla. */
export const CONTACT_CHANNEL_SHORT: Record<ContactChannel, string> = {
    call: 'Llamada',
    sms: 'SMS',
    chat: 'Chat App',
    whatsapp: 'WhatsApp',
    voice_blaster: 'Voz blaster',
};

/** Lo que ofrece el combo «Canal último contacto», en el orden del mock. */
export const CONTACT_CHANNEL_FILTER_OPTIONS: readonly {
    value: ContactChannelFilter;
    label: string;
}[] = [
    { value: 'call', label: 'Llamada' },
    { value: 'sms', label: 'SMS' },
    { value: 'chat', label: 'Chat App Fiado' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'voice_blaster', label: 'Voz blaster' },
    { value: 'no_contact_24h', label: 'Sin contacto en 24 h' },
    { value: 'no_contact_3d', label: 'Sin contacto en 3+ días' },
];

export const CONTACT_OUTCOME_LABEL: Record<ContactOutcome, string> = {
    spoke: 'Habló con cliente',
    promise: 'Promesa de pago',
    no_answer: 'No contesta',
    wrong_number: 'Número equivocado',
    voicemail: 'Buzón de voz',
    sent: 'Enviado',
};

/** Los mismos resultados, cortos, para el historial de contacto. */
export const CONTACT_OUTCOME_SHORT: Record<ContactOutcome, string> = {
    spoke: 'Habló',
    promise: 'Promesa de pago',
    no_answer: 'No contesta',
    wrong_number: 'Núm. equivocado',
    voicemail: 'Buzón',
    sent: 'Enviado',
};

export const PROMISE_STATE_LABEL: Record<PromiseState, string> = {
    active: 'Activo',
    fulfilled: 'Cumplido',
    broken: 'Incumplido',
};

export const PROMISE_STATE_TONE: Record<PromiseState, CreditTone> = {
    active: 'info',
    fulfilled: 'success',
    broken: 'danger',
};

export const PAYMENT_CHANNEL_LABEL: Record<PaymentChannel, string> = {
    store: 'Efectivo tienda',
    oxxo: 'OXXO',
    app: 'App Fiado (PCF)',
    pcf_auto: 'PCF automático',
    pcf_manual: 'App Fiado (PCF)',
};

/** El chip de estado del hero de la ficha. */
export function creditStatusChip(
    status: AgendaCreditStatus,
    overdueDays: number,
): { label: string; tone: CreditTone } {
    switch (status) {
        case 'pending_activation':
            return { label: 'Pend. activación', tone: 'warning' };
        case 'active':
            return { label: 'Activo', tone: 'success' };
        case 'in_arrears':
            return { label: `Mora ${String(overdueDays)}d`, tone: 'danger' };
        case 'settled':
            return { label: 'Liquidado', tone: 'info' };
        case 'cancelled':
            return { label: 'Cancelado (7d)', tone: 'neutral' };
        case 'discarded':
            return { label: 'Descartado', tone: 'neutral' };
        case 'written_off':
            return { label: 'Baja contable', tone: 'neutral' };
    }
}

/** Quién hizo el contacto: el nombre del cobrador, o la plataforma cuando fue automático. */
export function contactAuthorLabel(collectorId: string, collectorName: string | undefined): string {
    if (collectorId === 'system') return 'Sistema (automático)';
    return collectorName ?? collectorId;
}

export interface CommunicationTemplate {
    day: string;
    channel: string;
    tone: 'info' | 'warning' | 'danger';
    text: string;
}

/** Las 9 plantillas del ciclo de cobranza (M4 §6.3), con su texto tal cual el mock. */
export const COMMUNICATION_TEMPLATES: readonly CommunicationTemplate[] = [
    {
        day: '-2',
        channel: 'Push',
        tone: 'info',
        text: 'Tu cuota de $XXX vence el [día]. Revisa que tengas saldo en tu cuenta.',
    },
    {
        day: '-1',
        channel: 'Push + email',
        tone: 'info',
        text: 'Mañana vence tu cuota de $XXX. Paga hoy y mantén tu equipo activo.',
    },
    {
        day: '0',
        channel: 'Push + SMS + email',
        tone: 'warning',
        text: 'Tu cuota venció hoy. Paga antes de mañana para evitar el bloqueo de tu equipo.',
    },
    {
        day: '1',
        channel: 'Push + SMS',
        tone: 'danger',
        text: 'Tu equipo ha sido restringido por cuota vencida. Paga $XXX desde App Fiado o en tu tienda.',
    },
    {
        day: '3',
        channel: 'Push',
        tone: 'warning',
        text: 'Tu equipo sigue bloqueado. Paga $XXX para desbloquearlo. ¿Necesitas ayuda? Contáctanos.',
    },
    {
        day: '7',
        channel: 'Push + SMS',
        tone: 'warning',
        text: 'Llevas 7 días sin pagar. Tu equipo seguirá bloqueado hasta que pagues. Si tienes dificultades, acércate a tu tienda.',
    },
    {
        day: '14',
        channel: 'Push + SMS + Voz blaster',
        tone: 'danger',
        text: 'Tienes N cuotas vencidas ($XXX). Si no puedes pagar, podemos reestructurar tu crédito. Contacta a tu tienda.',
    },
    {
        day: '25',
        channel: 'Push + SMS',
        tone: 'danger',
        text: 'IMPORTANTE: Si no regularizas tu crédito antes del día 30, serás reportado a Círculo de Crédito. Esto puede afectar tu historial crediticio.',
    },
    {
        day: '30',
        channel: 'Push + SMS',
        tone: 'danger',
        text: 'Tu crédito ha sido reportado a Círculo de Crédito por $XXX de atraso. Paga para regularizarte.',
    },
];
